import { api } from './api.js';
import { generateUuidV7 } from '@edv-next/contracts/mutation';

export type CriterionKey = 'safety' | 'environment' | 'production' | 'detectability' | 'regulatory';
export type CriterionScore = { c?: number | null; l?: number | null };
export type Scores = Partial<Record<CriterionKey, CriterionScore>>;
export interface AcConfig {
  site_id: string;
  version: number;
  thresholds?: Record<string, unknown> | null;
  safety_override_c_threshold?: number;
  [key: string]: unknown;
}
export interface AcHistoryRow {
  id: string;
  asset_id: string;
  actor: string | null;
  at: string;
  action: string;
  before: unknown;
  after: unknown;
}
export interface AcImportPreview {
  site_id: string;
  create: Array<{ row_index: number; asset_id: string }>;
  update: Array<{ row_index: number; asset_id: string }>;
  duplicate: Array<{ row_index: number; eam_id: string }>;
  conflict: Array<{ row_index: number; asset_id: string; reason: string }>;
  unmapped: Array<{ row_index: number; eam_id: string }>;
  invalid: Array<{ row_index: number; reasons: string[] }>;
  as_of: string;
  applied?: AcImportApplyResult;
}
export interface AcImportApplyResult {
  batch_id: string;
  requested_rows: number;
  accepted: number;
  updated: number;
  skipped: number;
  rejected: number;
  mutation_ids: string[];
}

export interface AcAssessmentRow {
  asset_id: string;
  organization_id: string;
  site_id: string;
  ranking: 'Critical' | 'High' | 'Moderate' | 'Low' | null;
  score: number | null;
  complete: boolean;
  version: number;
  assessed_by: string | null;
  assessed_at: string | null;
  justification: string | null;
  status: 'draft' | 'active' | 'archived';
  archived_at: string | null;
  scores?: Scores;
}

export const acApi = {
  async getConfig(siteId: string) {
    return api.json<AcConfig>(`/api/v1/sites/${encodeURIComponent(siteId)}/ac/config`, {
      method: 'GET',
    });
  },
  async saveConfig(
    siteId: string,
    deviceId: string,
    expected_version: number | null,
    payload: Record<string, unknown>,
  ) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/sites/${encodeURIComponent(siteId)}/ac/config`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({ mutation_id: mid, device_id: deviceId, expected_version, payload }),
    });
  },
  async getScope(siteId: string) {
    return api.json<{ data: Array<{ asset_id: string; in_scope: boolean; updated_at: string }> }>(
      `/api/v1/sites/${encodeURIComponent(siteId)}/ac/scope`,
      { method: 'GET' },
    );
  },
  async replaceScope(
    siteId: string,
    deviceId: string,
    items: Array<{ asset_id: string; in_scope: boolean }>,
  ) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/sites/${encodeURIComponent(siteId)}/ac/scope`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        entity_id: siteId,
        payload: { items },
      }),
    });
  },
  async listAssessments(siteId: string, opts: { cursor?: string; limit?: number } = {}) {
    const qs = new URLSearchParams();
    if (opts.cursor) qs.set('cursor', opts.cursor);
    if (opts.limit) qs.set('limit', String(opts.limit));
    const q = qs.toString();
    return api.json<{ data: AcAssessmentRow[]; cursor: string | null; as_of: string }>(
      `/api/v1/sites/${encodeURIComponent(siteId)}/ac/assessments${q ? '?' + q : ''}`,
      { method: 'GET' },
    );
  },
  async getAssessment(assetId: string) {
    return api.json<AcAssessmentRow>(`/api/v1/ac/assessments/${encodeURIComponent(assetId)}`, {
      method: 'GET',
    });
  },
  async saveAssessment(
    assetId: string,
    deviceId: string,
    expected_version: number | null,
    payload: { scores?: Scores; justification?: string },
  ) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/ac/assessments/${encodeURIComponent(assetId)}`, {
      method: 'PUT',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        entity_id: assetId,
        expected_version,
        payload,
      }),
    });
  },
  async clearScores(assetId: string, deviceId: string, expected_version: number) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/ac/assessments/${encodeURIComponent(assetId)}/clear`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        entity_id: assetId,
        expected_version,
        payload: {},
      }),
    });
  },
  async copyScores(
    targetAssetId: string,
    sourceAssetId: string,
    deviceId: string,
    expected_version: number | null,
  ) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/ac/assessments/${encodeURIComponent(targetAssetId)}/copy`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        entity_id: targetAssetId,
        expected_version,
        payload: { source_asset_id: sourceAssetId },
      }),
    });
  },
  async archive(assetId: string, deviceId: string, expected_version: number) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/ac/assessments/${encodeURIComponent(assetId)}/archive`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        entity_id: assetId,
        expected_version,
        payload: {},
      }),
    });
  },
  async restore(assetId: string, deviceId: string, expected_version: number) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/ac/assessments/${encodeURIComponent(assetId)}/restore`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        entity_id: assetId,
        expected_version,
        payload: {},
      }),
    });
  },
  async history(siteId: string, assetId?: string, limit = 200) {
    const qs = new URLSearchParams();
    if (assetId) qs.set('asset_id', assetId);
    qs.set('limit', String(limit));
    return api.json<{ data: AcHistoryRow[] }>(
      `/api/v1/sites/${encodeURIComponent(siteId)}/ac/history?${qs}`,
      { method: 'GET' },
    );
  },
  async calcPreview(assessment: unknown, siteConfig?: unknown) {
    return api.json<unknown>(`/api/v1/ac/calc/preview`, {
      method: 'POST',
      body: JSON.stringify({ assessment, siteConfig }),
    });
  },
  async importPreview(
    siteId: string,
    rows: Array<{ eam_id: string; scores?: Scores; justification?: string }>,
  ) {
    return api.json<AcImportPreview>(
      `/api/v1/sites/${encodeURIComponent(siteId)}/ac/import/preview`,
      {
        method: 'POST',
        body: JSON.stringify({ site_id: siteId, rows }),
      },
    );
  },
  async importApply(
    siteId: string,
    deviceId: string,
    accepted: Array<{
      row_index: number;
      asset_id: string;
      scores?: Scores;
      justification?: string;
    }>,
  ) {
    const mid = generateUuidV7();
    return api.json<AcImportApplyResult>(
      `/api/v1/sites/${encodeURIComponent(siteId)}/ac/import/apply`,
      {
        method: 'POST',
        headers: { 'Idempotency-Key': mid },
        body: JSON.stringify({ site_id: siteId, mutation_id: mid, device_id: deviceId, accepted }),
      },
    );
  },
};
